import React, { useState } from 'react';
import { Shield, Check, X } from 'lucide-react';

interface PrivacyConsentModalProps {
  onAccept: () => void;
  onDecline: () => void;
}

const PrivacyConsentModal: React.FC<PrivacyConsentModalProps> = ({ onAccept, onDecline }) => {
  const [hasRead, setHasRead] = useState(false);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] flex flex-col">
        <div className="p-6 border-b border-neutral-200">
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8 text-primary-600" />
            <h2 className="text-2xl font-display font-bold text-neutral-900">
              Consentimento de Acesso aos Dados Clínicos
            </h2>
          </div>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          <div className="space-y-4 text-neutral-700">
            <p className="font-medium text-lg">
              Informações Importantes sobre seus Dados de Saúde
            </p>

            <p>
              Para garantir sua privacidade e segurança, é necessário seu consentimento
              para visualizar seus dados clínicos nesta plataforma.
            </p>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-blue-900 mb-2">O que você poderá visualizar:</h3>
              <ul className="list-disc list-inside space-y-1 text-blue-800">
                <li>Avaliações clínicas e resultados de exames funcionais</li>
                <li>Documentos e laudos médicos anexados</li>
                <li>Histórico de consultas e atendimentos</li>
                <li>Dados demográficos e informações de cadastro</li>
              </ul>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <h3 className="font-semibold text-amber-900 mb-2">Importantes Considerações:</h3>
              <ul className="list-disc list-inside space-y-1 text-amber-800">
                <li>Seus dados são protegidos por sigilo médico</li>
                <li>Apenas você e profissionais autorizados têm acesso</li>
                <li>Evoluções clínicas detalhadas devem ser solicitadas separadamente</li>
                <li>Você pode revogar este consentimento a qualquer momento</li>
              </ul>
            </div>

            <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-4">
              <h3 className="font-semibold text-neutral-900 mb-2">Seus Direitos:</h3>
              <ul className="list-disc list-inside space-y-1 text-neutral-700">
                <li>Acessar seus dados a qualquer momento</li>
                <li>Solicitar correções de informações incorretas</li>
                <li>Solicitar cópias de seus registros médicos</li>
                <li>Revogar o consentimento de acesso quando desejar</li>
              </ul>
            </div>

            <p className="text-sm text-neutral-600 italic">
              Ao aceitar este termo, você confirma que compreende e concorda com o acesso
              aos seus dados clínicos através desta plataforma, respeitando as normas da
              LGPD (Lei Geral de Proteção de Dados) e o sigilo profissional estabelecido
              pelo Conselho Federal de Fisioterapia e Terapia Ocupacional.
            </p>
          </div>

          <div className="mt-6">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={hasRead}
                onChange={(e) => setHasRead(e.target.checked)}
                className="mt-1 w-5 h-5 text-primary-600 rounded focus:ring-primary-500"
              />
              <span className="text-sm text-neutral-700">
                Li e compreendi todas as informações acima e desejo prosseguir com o
                acesso aos meus dados clínicos.
              </span>
            </label>
          </div>
        </div>

        <div className="p-6 border-t border-neutral-200 flex justify-end gap-3">
          <button
            onClick={onDecline}
            className="px-6 py-2 border border-neutral-300 rounded-lg text-neutral-700 hover:bg-neutral-50 transition-colors flex items-center gap-2"
          >
            <X className="w-4 h-4" />
            Não Aceito
          </button>
          <button
            onClick={onAccept}
            disabled={!hasRead}
            className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Check className="w-4 h-4" />
            Aceito e Prosseguir
          </button>
        </div>
      </div>
    </div>
  );
};

export default PrivacyConsentModal;
